/**
 * @module HypothesisReferenceMatches
 * @purpose Renders the per-tool "Reference matches" surface for a hypothesis — one compact card
 *   per distinct tool resolved by a reference_list predicate (e.g. attacker_toolkit), with its
 *   category, ATT&CK techniques, description, and the artefact types it was seen in.
 * @depends types/reasoning (ReferenceMatch), attackTechniques (techniqueName), react-i18next
 * @dependedOnBy HypothesisCard (dedicated walkthrough step, ADV-4)
 *
 * @rules
 * - Performance: pure display — props only, no state, no data fetching
 * - State: none
 * - INVARIANT: descriptive metadata only — no scoring, ranking, or confidence language.
 * - Errors: n/a — renders a neutral empty state when entries is empty
 */

import { useTranslation } from 'react-i18next'
import type { ReferenceMatch } from '../../../../../types/reasoning'
import { techniqueName } from './attack-techniques'

interface HypothesisReferenceMatchesProps {
  entries: ReferenceMatch[]
}

interface ReferenceMatchCardProps {
  entry: ReferenceMatch
}

const CONTAINER_STYLE: React.CSSProperties = {
  padding: '4px 0',
}

const SECTION_HEADING: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: '8px',
}

const TOOL_CARD: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  padding: '8px 10px',
  marginBottom: '6px',
}

const TOOL_NAME_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '4px',
}

const CHIP_BASE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.58rem',
  padding: '1px 6px',
  borderRadius: '4px',
  display: 'inline-block',
  marginRight: '4px',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
  background: 'transparent',
}

const ACCENT_CHIP: React.CSSProperties = {
  ...CHIP_BASE,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
}

const NOTE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.75rem',
  color: 'var(--text-dim)',
  marginTop: '6px',
}

const META_ROW: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.72rem',
  color: 'var(--text-dim)',
  marginTop: '4px',
}

const META_LABEL: React.CSSProperties = {
  color: 'var(--text-muted)',
  marginRight: '4px',
}

const EMPTY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.78rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
}

function ReferenceMatchCard({ entry }: ReferenceMatchCardProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div style={TOOL_CARD}>
      <div style={TOOL_NAME_STYLE}>{entry.name}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '4px' }}>
        <span style={ACCENT_CHIP}>{entry.category}</span>
        {entry.attack.map((id) => (
          <span key={id} style={CHIP_BASE}>{id} — {techniqueName(id)}</span>
        ))}
      </div>
      {entry.note !== '' && (
        <div style={NOTE_STYLE}>
          <span style={META_LABEL}>{t('reasoning.hypothesis.referenceMatches.descriptionLabel')}:</span>
          {' '}{entry.note}
        </div>
      )}
      <div style={META_ROW}>
        <span style={META_LABEL}>{t('reasoning.hypothesis.referenceMatches.evidenceInLabel')}:</span>
        {entry.artefact_types.join(', ')}
      </div>
    </div>
  )
}

/**
 * Renders the reference-matches step — per-tool cards resolved from a reference_list predicate.
 *
 * @behaviour Displays each distinct matched tool as a card with name, category chip, ATT&CK
 *   technique chips (id + resolved name), description, and the artefact types it was seen in.
 *   Empty state renders a neutral message when no reference_list matcher fired for this
 *   hypothesis. Purely descriptive — no scoring, ranking, or confidence language.
 * @errors n/a
 */
export function HypothesisReferenceMatches({ entries }: HypothesisReferenceMatchesProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  return (
    <div style={CONTAINER_STYLE}>
      <div style={SECTION_HEADING}>{t('reasoning.hypothesis.referenceMatches.title')}</div>
      {entries.length === 0
        ? <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.referenceMatches.empty')}</div>
        : entries.map((entry) => <ReferenceMatchCard key={entry.name} entry={entry} />)
      }
    </div>
  )
}
